import React, { useState, useEffect } from 'react';
import { useAdmin } from '../context/AdminContext';
import { uploadToCloudinary } from '../utils/cloudinary';

const DestinationGalleryManager = ({ setMessage }) => {
    const { apiRequest } = useAdmin();
    const [galleryItems, setGalleryItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [imageUploading, setImageUploading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [manualImageUrl, setManualImageUrl] = useState('');
    const [showUrlInput, setShowUrlInput] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);

    const [formData, setFormData] = useState({
        title: '',
        discount: '20% OFF',
        image: '',
        position: 'medium-top',
        order: 0
    });

    // Inline CSS Styles
    const styles = {
        galleryManager: {
            minHeight: '100vh',
            backgroundColor: '#f8f9fa',
            position: 'relative'
        },
        header: {
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            padding: '20px 0',
            marginBottom: '30px'
        },
        card: {
            borderRadius: '12px',
            border: '1px solid #e0e0e0',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease'
        },
        cardHover: {
            transform: 'translateY(-5px)',
            boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
        },
        galleryItem: {
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '10px',
            cursor: 'pointer'
        },
        galleryImageContainer: {
            position: 'relative',
            width: '100%',
            height: '250px',
            overflow: 'hidden'
        },
        galleryImage: {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            transition: 'transform 0.5s ease'
        },
        galleryImageHover: {
            transform: 'scale(1.05)'
        },
        badge: {
            position: 'absolute',
            padding: '5px 12px',
            borderRadius: '20px',
            fontSize: '0.85rem',
            fontWeight: '600',
            zIndex: '10'
        },
        discountBadge: {
            top: '15px',
            left: '15px',
            background: 'linear-gradient(135deg, #ff6b6b, #ff4757)',
            color: 'white',
            boxShadow: '0 4px 10px rgba(255,107,107,0.3)'
        },
        titleBadge: {
            bottom: '15px',
            right: '15px',
            background: 'rgba(255,255,255,0.95)',
            color: '#2d3436',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            maxWidth: '80%',
            textAlign: 'center'
        },
        positionBadge: {
            top: '15px',
            right: '15px',
            background: '#0d6efd',
            color: 'white'
        },
        statusBadge: {
            bottom: '15px',
            left: '15px'
        },
        imageOverlay: {
            position: 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            height: '100%',
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.3) 100%)'
        },
        previewContainer: {
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '8px',
            backgroundColor: '#f8f9fa',
            margin: '0 auto'
        },
        previewLarge: {
            height: '200px',
            width: '100%'
        },
        previewMedium: {
            height: '150px',
            width: '100%'
        },
        previewSmall: {
            height: '120px',
            width: '100%'
        },
        previewImage: {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center'
        },
        modalBackdrop: {
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px',
            backdropFilter: 'blur(5px)'
        },
        modalContent: {
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            width: '100%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'hidden',
            position: 'relative',
            animation: 'modalFadeIn 0.3s ease-out'
        },
        modalHeader: {
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            padding: '20px 30px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px'
        },
        modalBody: {
            padding: '30px',
            maxHeight: 'calc(90vh - 140px)',
            overflowY: 'auto'
        },
        modalFooter: {
            padding: '20px 30px',
            borderTop: '1px solid #e0e0e0',
            background: '#f8f9fa',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px'
        },
        closeButton: {
            background: 'none',
            border: 'none',
            color: 'white',
            fontSize: '24px',
            cursor: 'pointer',
            padding: '0',
            width: '30px',
            height: '30px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            transition: 'all 0.3s ease'
        },
        closeButtonHover: {
            backgroundColor: 'rgba(255,255,255,0.2)',
            transform: 'rotate(90deg)'
        },
        sectionTitle: {
            position: 'relative',
            display: 'inline-block',
            padding: '0 1rem'
        },
        layoutGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '10px',
            minHeight: '200px'
        },
        layoutCell: {
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            padding: '10px',
            height: '100%',
            textAlign: 'center'
        },
        layoutImage: {
            height: '80px',
            width: '100%',
            objectFit: 'cover',
            borderRadius: '4px'
        },
        loadingSpinner: {
            width: '3rem',
            height: '3rem'
        },
        buttonGroup: {
            display: 'flex',
            gap: '5px'
        },
        uploadOptions: {
            display: 'flex',
            gap: '10px',
            marginBottom: '15px'
        },
        optionButton: {
            flex: 1,
            padding: '12px',
            borderRadius: '8px',
            border: '2px solid #dee2e6',
            background: 'white',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            fontWeight: '500',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        },
        optionButtonActive: {
            borderColor: '#0d6efd',
            background: '#e7f1ff',
            color: '#0d6efd',
            boxShadow: '0 3px 10px rgba(13, 110, 253, 0.2)'
        },
        inputGroup: {
            marginBottom: '20px'
        },
        formLabel: {
            display: 'block',
            marginBottom: '8px',
            fontWeight: '600',
            color: '#333'
        },
        formControl: {
            width: '100%',
            padding: '12px 15px',
            border: '1px solid #ced4da',
            borderRadius: '8px',
            fontSize: '14px',
            transition: 'all 0.3s ease'
        },
        formControlFocus: {
            borderColor: '#0d6efd',
            boxShadow: '0 0 0 0.2rem rgba(13, 110, 253, 0.25)'
        },
        primaryButton: {
            backgroundColor: '#0d6efd',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        },
        primaryButtonHover: {
            backgroundColor: '#0b5ed7',
            transform: 'translateY(-2px)',
            boxShadow: '0 5px 15px rgba(13, 110, 253, 0.3)'
        },
        primaryButtonDisabled: {
            backgroundColor: '#6c757d',
            cursor: 'not-allowed',
            opacity: 0.7
        },
        secondaryButton: {
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
        },
        secondaryButtonHover: {
            backgroundColor: '#5c636a'
        }
    };

    // Animation keyframes
    const animationStyles = `
        @keyframes modalFadeIn {
            from {
                opacity: 0;
                transform: translateY(-50px) scale(0.9);
            }
            to {
                opacity: 1;
                transform: translateY(0) scale(1);
            }
        }
        
        @keyframes fadeIn {
            from {
                opacity: 0;
            }
            to {
                opacity: 1;
            }
        }
        
        body.modal-open {
            overflow: hidden;
            padding-right: 15px; /* Prevent layout shift from scrollbar */
        }
    `;

    // Position options with recommended image dimensions
    const positionOptions = [
        {
            value: 'large-left',
            label: 'Large Left',
            description: '500px height - Left column large image',
            recommendedSize: '1200x600px (Landscape)',
            containerClass: 'gallery-item-large',
            previewStyle: styles.previewLarge,
            gridClass: 'col-lg-7 col-md-6'
        },
        {
            value: 'large-right',
            label: 'Large Right',
            description: '500px height - Right column large image',
            recommendedSize: '800x500px (Portrait)',
            containerClass: 'gallery-item-large',
            previewStyle: styles.previewLarge,
            gridClass: 'col-lg-5 col-md-6'
        },
        {
            value: 'medium-top',
            label: 'Medium Top',
            description: '300px height - Top medium image',
            recommendedSize: '1200x400px (Wide Landscape)',
            containerClass: 'gallery-item-medium',
            previewStyle: styles.previewMedium,
            gridClass: 'col-lg-12 col-md-12'
        },
        {
            value: 'medium-bottom',
            label: 'Medium Bottom',
            description: '250px height - Bottom medium image',
            recommendedSize: '600x250px (Landscape)',
            containerClass: 'gallery-item-medium',
            previewStyle: styles.previewMedium,
            gridClass: 'col-lg-6 col-md-6'
        },
        {
            value: 'small-1',
            label: 'Small 1',
            description: '200px height - First small image',
            recommendedSize: '400x200px (Square/Landscape)',
            containerClass: 'gallery-item-small',
            previewStyle: styles.previewSmall,
            gridClass: 'col-lg-6 col-md-6'
        },
        {
            value: 'small-2',
            label: 'Small 2',
            description: '200px height - Second small image',
            recommendedSize: '400x200px (Square/Landscape)',
            containerClass: 'gallery-item-small',
            previewStyle: styles.previewSmall,
            gridClass: 'col-lg-6 col-md-6'
        }
    ];

    useEffect(() => {
        loadGalleryItems();
        // Add animation styles
        const styleSheet = document.createElement('style');
        styleSheet.textContent = animationStyles;
        document.head.appendChild(styleSheet);

        return () => {
            document.head.removeChild(styleSheet);
            document.body.classList.remove('modal-open');
        };
    }, []);

    useEffect(() => {
        if (showForm) {
            document.body.classList.add('modal-open');
            setModalVisible(true);
        } else {
            document.body.classList.remove('modal-open');
            setModalVisible(false);
        }
    }, [showForm]);

    const loadGalleryItems = async () => {
        try {
            setLoading(true);
            const data = await apiRequest('/destination-gallery/admin/all');

            if (data.success) {
                setGalleryItems(data.galleryItems || []);
            }
        } catch (error) {
            console.error('Failed to load gallery items:', error);
            setMessage({
                type: 'error',
                text: 'Error loading gallery items: ' + (error.message || 'Please try again')
            });
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            setMessage({ type: 'error', text: 'Please select a valid image file (JPEG, PNG, WebP)' });
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setMessage({ type: 'error', text: 'Image size should be less than 5MB' });
            return;
        }

        try {
            setImageUploading(true);
            setMessage({ type: 'info', text: 'Uploading image to Cloudinary...' });

            const result = await uploadToCloudinary(file);

            setFormData(prev => ({
                ...prev,
                image: result.url
            }));

            setMessage({ type: 'success', text: 'Image uploaded successfully!' });
            setShowUrlInput(false);
        } catch (error) {
            console.error('Cloudinary upload error:', error);
            setMessage({ type: 'error', text: 'Error uploading image: ' + error.message });
        } finally {
            setImageUploading(false);
        }
    };

    const handleManualImageUrl = () => {
        if (manualImageUrl.trim()) {
            try {
                // Basic URL validation
                new URL(manualImageUrl);
                setFormData(prev => ({
                    ...prev,
                    image: manualImageUrl.trim()
                }));
                setManualImageUrl('');
                setMessage({ type: 'success', text: 'Image URL added successfully!' });
                setShowUrlInput(false);
            } catch (error) {
                setMessage({ type: 'error', text: 'Please enter a valid URL (e.g., https://example.com/image.jpg)' });
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.title || !formData.image || !formData.position) {
            setMessage({ type: 'error', text: 'Title, image, and position are required' });
            return;
        }

        try {
            setLoading(true);

            const params = editingItem ? { force: 'true' } : {};
            const queryString = new URLSearchParams(params).toString();

            if (editingItem) {
                await apiRequest(`/destination-gallery/admin/update/${editingItem._id}?${queryString}`, 'PUT', formData);
                setMessage({ type: 'success', text: 'Gallery item updated successfully!' });
            } else {
                await apiRequest(`/destination-gallery/admin/create?${queryString}`, 'POST', formData);
                setMessage({ type: 'success', text: 'Gallery item created successfully!' });
            }

            await loadGalleryItems();
            resetForm();
        } catch (error) {
            console.error('Save gallery item error:', error);

            if (error.message.includes('already occupied')) {
                setMessage({
                    type: 'error',
                    text: error.message,
                    showForceOption: true
                });
            } else {
                setMessage({ type: 'error', text: 'Error: ' + error.message });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (item) => {
        setEditingItem(item);
        setFormData({
            title: item.title,
            discount: item.discount,
            image: item.image,
            position: item.position,
            order: item.order || 0
        });
        setShowForm(true);
        setShowUrlInput(false);
        setManualImageUrl('');
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this gallery item?')) {
            return;
        }

        try {
            setLoading(true);
            await apiRequest(`/destination-gallery/admin/delete/${id}`, 'DELETE');
            setMessage({ type: 'success', text: 'Gallery item deleted successfully!' });
            await loadGalleryItems();
        } catch (error) {
            console.error('Delete gallery item error:', error);
            setMessage({ type: 'error', text: 'Error deleting item: ' + error.message });
        } finally {
            setLoading(false);
        }
    };

    const handleToggleStatus = async (item) => {
        try {
            await apiRequest(`/destination-gallery/admin/update/${item._id}`, 'PUT', {
                isActive: !item.isActive
            });

            setMessage({ type: 'success', text: `Item ${!item.isActive ? 'activated' : 'deactivated'} successfully!` });
            await loadGalleryItems();
        } catch (error) {
            console.error('Toggle status error:', error);
            setMessage({ type: 'error', text: 'Error updating status: ' + error.message });
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            discount: '20% OFF',
            image: '',
            position: 'medium-top',
            order: 0
        });
        setEditingItem(null);
        setShowForm(false);
        setShowUrlInput(false);
        setManualImageUrl('');
        setModalVisible(false);
        document.body.classList.remove('modal-open');
    };

    const removeImage = () => {
        setFormData(prev => ({
            ...prev,
            image: ''
        }));
    };

    const getPositionLabel = (position) => {
        const option = positionOptions.find(opt => opt.value === position);
        return option ? option.label : position;
    };

    const getPositionBadgeColor = (position) => {
        const colors = {
            'large-left': 'danger',
            'large-right': 'primary',
            'medium-top': 'warning',
            'medium-bottom': 'info',
            'small-1': 'success',
            'small-2': 'secondary'
        };
        return colors[position] || 'secondary';
    };

    const getCurrentPositionInfo = () => {
        return positionOptions.find(opt => opt.value === formData.position);
    };

    const filteredItems = galleryItems.filter(item =>
        item.title.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const renderGalleryItemCard = (item) => {
        const positionInfo = positionOptions.find(opt => opt.value === item.position);

        return (
            <div className="col-md-6 col-lg-4 mb-4" key={item._id}>
                <div
                    className="card h-100 shadow-sm"
                    style={styles.card}
                    onMouseEnter={(e) => e.currentTarget.style.transform = styles.cardHover.transform}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'none'}
                >
                    <div style={{ ...styles.galleryImageContainer, height: item.position.includes('large') ? '300px' : item.position.includes('medium') ? '200px' : '150px' }}>
                        <img
                            src={item.image}
                            alt={item.title}
                            style={styles.galleryImage}
                            onMouseEnter={(e) => e.currentTarget.style.transform = styles.galleryImageHover.transform}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'none'}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = `https://via.placeholder.com/400x${item.position.includes('large') ? '300' : item.position.includes('medium') ? '200' : '150'}/f8f9fa/6c757d?text=${encodeURIComponent(item.title)}`;
                            }}
                        />
                        <div style={styles.imageOverlay}></div>

                        {/* Position Badge */}
                        <div style={{ ...styles.badge, ...styles.positionBadge, background: `var(--bs-${getPositionBadgeColor(item.position)})` }}>
                            {getPositionLabel(item.position)}
                        </div>

                        {/* Discount Badge */}
                        <div style={{ ...styles.badge, ...styles.discountBadge }}>
                            {item.discount}
                        </div>

                        {/* Status Badge */}
                        <div style={{ ...styles.badge, ...styles.statusBadge, background: item.isActive ? 'var(--bs-success)' : 'var(--bs-secondary)' }}>
                            {item.isActive ? 'Active' : 'Inactive'}
                        </div>
                    </div>

                    <div className="card-body">
                        <h5 className="card-title mb-2">{item.title}</h5>
                        <p className="small text-muted mb-2">
                            <strong>Position:</strong> {positionInfo?.label}
                            <br />
                            <strong>Recommended Size:</strong> {positionInfo?.recommendedSize}
                            <br />
                            <strong>Grid Class:</strong> {positionInfo?.gridClass}
                        </p>
                        <div className="d-flex justify-content-between align-items-center">
                            <small className="text-muted">
                                Order: <strong>{item.order}</strong>
                            </small>
                            <div className="btn-group btn-group-sm">
                                <button
                                    className="btn btn-outline-primary"
                                    onClick={() => handleEdit(item)}
                                    title="Edit"
                                >
                                    <i className="fas fa-edit"></i>
                                </button>
                                <button
                                    className={`btn btn-outline-${item.isActive ? 'warning' : 'success'}`}
                                    onClick={() => handleToggleStatus(item)}
                                    title={item.isActive ? 'Deactivate' : 'Activate'}
                                >
                                    <i className={`fas ${item.isActive ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                </button>
                                <button
                                    className="btn btn-outline-danger"
                                    onClick={() => handleDelete(item._id)}
                                    title="Delete"
                                >
                                    <i className="fas fa-trash"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div style={styles.galleryManager}>
            {/* Header */}
            <div style={styles.header}>
                <div className="container">
                    <div className="d-flex justify-content-between align-items-center">
                        <h1 className="mb-0">
                            <i className="fas fa-images me-3"></i>
                            Destination Gallery Management
                        </h1>
                        <button
                            className="btn btn-light btn-lg"
                            onClick={() => setShowForm(true)}
                            disabled={loading}
                        >
                            <i className="fas fa-plus me-2"></i>
                            Add Gallery Item
                        </button>
                    </div>
                </div>
            </div>

            <div className="container">
                {/* Search Bar */}
                <div className="card mb-4" style={styles.card}>
                    <div className="card-body">
                        <div className="input-group">
                            <span className="input-group-text">
                                <i className="fas fa-search"></i>
                            </span>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search gallery items by title..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            {searchTerm && (
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={() => setSearchTerm('')}
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Layout Preview */}
                <div className="card mb-4" style={styles.card}>
                    <div className="card-header bg-primary text-white">
                        <h5 className="mb-0">
                            <i className="fas fa-th-large me-2"></i>
                            Gallery Layout Preview
                        </h5>
                    </div>
                    <div className="card-body">
                        <div style={styles.layoutGrid}>
                            {positionOptions.map(position => {
                                const item = galleryItems.find(i => i.position === position.value && i.isActive);
                                return (
                                    <div key={position.value} style={styles.layoutCell}>
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <small className="fw-bold">{position.label}</small>
                                            <small className={`badge bg-${item ? 'success' : 'secondary'}`}>
                                                {item ? '✓ Occupied' : 'Empty'}
                                            </small>
                                        </div>
                                        {item ? (
                                            <div>
                                                <img
                                                    src={item.image}
                                                    alt={item.title}
                                                    style={styles.layoutImage}
                                                />
                                                <small className="d-block mt-1 text-truncate">{item.title}</small>
                                            </div>
                                        ) : (
                                            <div className="text-center text-muted py-3">
                                                <i className="fas fa-image fa-2x mb-2"></i>
                                                <small className="d-block">No image</small>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Gallery Items List */}
                {loading && !showForm ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" style={styles.loadingSpinner} role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3">Loading gallery items...</p>
                    </div>
                ) : filteredItems.length === 0 ? (
                    <div className="text-center py-5">
                        <i className="fas fa-images fa-3x text-muted mb-3"></i>
                        <h3 className="text-muted">No gallery items found</h3>
                        <p className="text-muted mb-4">Add your first gallery item to get started</p>
                        <button
                            className="btn btn-primary btn-lg"
                            onClick={() => setShowForm(true)}
                        >
                            <i className="fas fa-plus me-2"></i>
                            Add First Gallery Item
                        </button>
                    </div>
                ) : (
                    <div className="row">
                        {filteredItems.map(renderGalleryItemCard)}
                    </div>
                )}

                {/* Statistics */}
                {galleryItems.length > 0 && (
                    <div className="card mt-4" style={styles.card}>
                        <div className="card-header bg-info text-white">
                            <h5 className="mb-0">
                                <i className="fas fa-chart-bar me-2"></i>
                                Gallery Statistics
                            </h5>
                        </div>
                        <div className="card-body">
                            <div className="row text-center">
                                <div className="col-md-3 mb-3">
                                    <div className="card bg-primary text-white">
                                        <div className="card-body">
                                            <h2 className="mb-0">{galleryItems.length}</h2>
                                            <small>Total Items</small>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3 mb-3">
                                    <div className="card bg-success text-white">
                                        <div className="card-body">
                                            <h2 className="mb-0">{galleryItems.filter(item => item.isActive).length}</h2>
                                            <small>Active Items</small>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3 mb-3">
                                    <div className="card bg-warning text-dark">
                                        <div className="card-body">
                                            <h2 className="mb-0">{galleryItems.filter(item => !item.isActive).length}</h2>
                                            <small>Inactive Items</small>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3 mb-3">
                                    <div className="card bg-secondary text-white">
                                        <div className="card-body">
                                            <h2 className="mb-0">{positionOptions.length}</h2>
                                            <small>Available Positions</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal for Add/Edit Form */}
            {modalVisible && (
                <div
                    style={styles.modalBackdrop}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            resetForm();
                        }
                    }}
                >
                    <div style={styles.modalContent}>
                        <div style={styles.modalHeader}>
                            <h5 className="modal-title mb-0">
                                <i className={`fas ${editingItem ? 'fa-edit' : 'fa-plus'} me-2`}></i>
                                {editingItem ? 'Edit Gallery Item' : 'Add New Gallery Item'}
                            </h5>
                            <button
                                type="button"
                                style={styles.closeButton}
                                onClick={resetForm}
                                onMouseEnter={(e) => e.currentTarget.style.transform = styles.closeButtonHover.transform}
                                onMouseLeave={(e) => e.currentTarget.style.transform = 'none'}
                                disabled={loading || imageUploading}
                            >
                                ×
                            </button>
                        </div>
                        <div style={styles.modalBody}>
                            <form onSubmit={handleSubmit}>
                                <div className="row g-3">
                                    <div className="col-md-6" style={styles.inputGroup}>
                                        <label style={styles.formLabel}>
                                            Title *
                                        </label>
                                        <input
                                            type="text"
                                            name="title"
                                            value={formData.title}
                                            onChange={handleChange}
                                            style={styles.formControl}
                                            placeholder="e.g., Thailand Beach"
                                            required
                                            disabled={loading}
                                            onFocus={(e) => e.currentTarget.style = { ...styles.formControl, ...styles.formControlFocus }}
                                            onBlur={(e) => e.currentTarget.style = styles.formControl}
                                        />
                                    </div>

                                    <div className="col-md-6" style={styles.inputGroup}>
                                        <label style={styles.formLabel}>
                                            Discount Text
                                        </label>
                                        <input
                                            type="text"
                                            name="discount"
                                            value={formData.discount}
                                            onChange={handleChange}
                                            style={styles.formControl}
                                            placeholder="e.g., 20% OFF"
                                            disabled={loading}
                                        />
                                    </div>

                                    <div className="col-12" style={styles.inputGroup}>
                                        <label style={styles.formLabel}>
                                            Position in Gallery *
                                        </label>
                                        <select
                                            name="position"
                                            value={formData.position}
                                            onChange={handleChange}
                                            style={styles.formControl}
                                            disabled={loading}
                                            required
                                        >
                                            {positionOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label} - {option.description} ({option.recommendedSize})
                                                </option>
                                            ))}
                                        </select>
                                        <div className="alert alert-info mt-2 mb-0 p-2 small">
                                            <i className="fas fa-info-circle me-1"></i>
                                            <strong>Recommended Size:</strong> {getCurrentPositionInfo()?.recommendedSize}
                                        </div>
                                    </div>

                                    <div className="col-md-6" style={styles.inputGroup}>
                                        <label style={styles.formLabel}>
                                            Display Order
                                        </label>
                                        <input
                                            type="number"
                                            name="order"
                                            value={formData.order}
                                            onChange={handleChange}
                                            style={styles.formControl}
                                            placeholder="0"
                                            min="0"
                                            disabled={loading}
                                        />
                                        <div className="form-text mt-1">
                                            Lower numbers appear first
                                        </div>
                                    </div>

                                    <div className="col-12" style={styles.inputGroup}>
                                        <label style={styles.formLabel}>
                                            Image *
                                        </label>

                                        {/* Image Upload Options */}
                                        <div style={styles.uploadOptions}>
                                            <button
                                                type="button"
                                                style={{
                                                    ...styles.optionButton,
                                                    ...(!showUrlInput ? styles.optionButtonActive : {})
                                                }}
                                                onClick={() => setShowUrlInput(false)}
                                                disabled={loading || imageUploading}
                                            >
                                                <i className="fas fa-cloud-upload-alt me-2"></i>
                                                Upload Image
                                            </button>
                                            <button
                                                type="button"
                                                style={{
                                                    ...styles.optionButton,
                                                    ...(showUrlInput ? styles.optionButtonActive : {})
                                                }}
                                                onClick={() => setShowUrlInput(true)}
                                                disabled={loading || imageUploading}
                                            >
                                                <i className="fas fa-link me-2"></i>
                                                Enter URL
                                            </button>
                                        </div>

                                        {/* File Upload Section */}
                                        {!showUrlInput ? (
                                            <div className="mb-3">
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handleImageUpload}
                                                    style={styles.formControl}
                                                    disabled={loading || imageUploading}
                                                />
                                                <div className="form-text mt-1">
                                                    <strong>Recommendations:</strong>
                                                    <ul className="mb-0 ps-3 small">
                                                        <li>Format: JPG, PNG, WebP</li>
                                                        <li>Max size: 5MB</li>
                                                        <li>Optimal dimensions: {getCurrentPositionInfo()?.recommendedSize}</li>
                                                        <li>Use high-quality landscape images for better fit</li>
                                                        <li>Image will be automatically cropped to fit the frame</li>
                                                    </ul>
                                                </div>
                                            </div>
                                        ) : (
                                            /* Manual URL Input Section */
                                            <div className="mb-3">
                                                <div className="input-group">
                                                    <input
                                                        type="url"
                                                        style={styles.formControl}
                                                        placeholder="https://example.com/image.jpg"
                                                        value={manualImageUrl}
                                                        onChange={(e) => setManualImageUrl(e.target.value)}
                                                        disabled={loading || imageUploading}
                                                    />
                                                    <button
                                                        type="button"
                                                        style={{ ...styles.primaryButton, marginLeft: '10px' }}
                                                        onClick={handleManualImageUrl}
                                                        disabled={loading || imageUploading || !manualImageUrl.trim()}
                                                    >
                                                        <i className="fas fa-check me-2"></i>
                                                        Use URL
                                                    </button>
                                                </div>
                                                <div className="form-text mt-1">
                                                    <strong>URL Guidelines:</strong>
                                                    <ul className="mb-0 ps-3 small">
                                                        <li>Enter a direct image URL (ending with .jpg, .png, .webp)</li>
                                                        <li>Make sure the image is publicly accessible</li>
                                                        <li>Recommended image hosting: Cloudinary, Imgur, or your own server</li>
                                                        <li>Use HTTPS URLs for better security</li>
                                                    </ul>
                                                </div>
                                            </div>
                                        )}

                                        {imageUploading && (
                                            <div className="alert alert-info mt-2 mb-0">
                                                <div className="d-flex align-items-center">
                                                    <div className="spinner-border spinner-border-sm me-2" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                    <span>Uploading image to Cloudinary...</span>
                                                </div>
                                            </div>
                                        )}

                                        {formData.image && (
                                            <div className="mt-3 p-3 border rounded bg-light">
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <strong className="text-success">
                                                        <i className="fas fa-check-circle me-1"></i>
                                                        {formData.image.startsWith('http') ? 'Image URL Added' : 'Image Uploaded'}
                                                    </strong>
                                                    <div className="btn-group">
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-info btn-sm"
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(formData.image);
                                                                setMessage({ type: 'success', text: 'Image URL copied to clipboard!' });
                                                            }}
                                                            disabled={loading || imageUploading}
                                                        >
                                                            <i className="fas fa-copy me-1"></i>
                                                            Copy URL
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={removeImage}
                                                            className="btn btn-outline-danger btn-sm"
                                                            disabled={loading || imageUploading}
                                                        >
                                                            <i className="fas fa-times me-1"></i>
                                                            Remove
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="text-center">
                                                    <div style={{ ...styles.previewContainer, ...getCurrentPositionInfo()?.previewStyle }}>
                                                        <img
                                                            src={formData.image}
                                                            alt="Preview"
                                                            style={styles.previewImage}
                                                            onError={(e) => {
                                                                e.target.onerror = null;
                                                                e.target.style.display = 'none';
                                                                e.target.parentElement.innerHTML = `
                                                                    <div class="text-center py-5">
                                                                        <i class="fas fa-exclamation-triangle fa-2x text-warning mb-3"></i>
                                                                        <p class="text-muted small">Failed to load image preview</p>
                                                                        <p class="text-muted small">URL: ${formData.image}</p>
                                                                    </div>
                                                                `;
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="mt-2 small text-muted">
                                                        <i className="fas fa-info-circle me-1"></i>
                                                        This is how your image will appear in the gallery
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </form>
                        </div>
                        <div style={styles.modalFooter}>
                            <button
                                type="button"
                                style={styles.secondaryButton}
                                onClick={resetForm}
                                disabled={loading || imageUploading}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                style={{
                                    ...styles.primaryButton,
                                    ...(loading || imageUploading || !formData.image ? styles.primaryButtonDisabled : {})
                                }}
                                onClick={handleSubmit}
                                disabled={loading || imageUploading || !formData.image}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        {editingItem ? 'Updating...' : 'Creating...'}
                                    </>
                                ) : (
                                    <>
                                        <i className={`fas ${editingItem ? 'fa-save' : 'fa-plus'} me-2`}></i>
                                        {editingItem ? 'Update Item' : 'Create Item'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DestinationGalleryManager;